import { Fragment, useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { REQUEST } from '@/constants/api';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import {
  selectBasicInfoStatus,
  selectUserData,
  selectUserHolderJointMinor,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  DocumentType,
  RequiredDocumentSection,
} from '@/types/document';
import { User } from '@/types/user';

import { isExistingCustomer } from '@/utils/user';

import { RequiredDocumentSelectDrawer } from '../drawers/RequiredDocumentSelectDrawer';

export const ProofOfRelationship = () => {
  const { t } = useTranslate();

  const { isProofOfRelationshipBlockedByBasicInfo = true } =
    useAppSelector(selectConfigsState);

  const { user, requiredDocumentSections, documentByDocType } =
    useAppSelector(selectUserData);
  const minorHolder = useAppSelector(selectUserHolderJointMinor);

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);

  const isBasicInfoCompleted =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved;

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isDrawerOpen);

  const requiredProofOfRelationshipSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfRelationship,
  );
  const proofOfRelationshipSection = useMemo(() => {
    if (
      !requiredProofOfRelationshipSection ||
      !user?.operationId ||
      minorHolder?.clientId === undefined
    ) {
      return requiredProofOfRelationshipSection;
    }

    const downloadPath = REQUEST.OPERATION.SUPPORTING_LETTER(
      user.operationId,
      minorHolder.clientId,
    );
    return {
      ...requiredProofOfRelationshipSection,
      groups: requiredProofOfRelationshipSection.groups?.map(group => ({
        ...group,
        items: group.items.map(item =>
          item.name === DocumentType.SupportingLetter
            ? {
                ...item,
                downloadPath,
                downloadFileName: 'Supporting_Letter.docx',
              }
            : item,
        ),
      })),
    };
  }, [
    minorHolder?.clientId,
    requiredProofOfRelationshipSection,
    user?.operationId,
  ]);

  const checkBtnDisableForEXC = (
    section: RequiredDocumentSection,
    isNotPendingAndNoDocs: boolean,
  ) => {
    if (!(user as User).clientReviewed) {
      return true;
    } else {
      if (section.status === DocumentStatus.Approved) {
        return true;
      }
      /**
       * If the EXC has already signed/uploaded section docs then set it true,
       * otherwise set it false
       */
      return isNotPendingAndNoDocs;
    }
  };

  const isButtonDisabled = (section: RequiredDocumentSection) => {
    if (!user) return true;
    const isNotPendingAndNoDocs =
      section.status !== DocumentStatus.Pending && !section.docs.length;
    if (user && isExistingCustomer(user)) {
      return checkBtnDisableForEXC(section, isNotPendingAndNoDocs);
    }

    return isNotPendingAndNoDocs;
  };

  if (!proofOfRelationshipSection) {
    return null;
  }

  return (
    <Fragment key={DocumentSection.ProofOfRelationship}>
      <DocumentActionItem
        title={t('documentSection.Proof_of_Relationship.title')}
        description={t('documentSection.Proof_of_Relationship.description')}
        onClick={openDrawer}
        status={proofOfRelationshipSection.status}
        enabledForAllStatuses
        disabled={
          isProofOfRelationshipBlockedByBasicInfo && !isBasicInfoCompleted
        }
        disabledButton={isButtonDisabled(proofOfRelationshipSection)}
      />
      <RequiredDocumentSelectDrawer
        open={isDrawerOpen}
        section={proofOfRelationshipSection}
        onClose={closeDrawer}
        documentByDocType={documentByDocType}
      />
    </Fragment>
  );
};
